// Utility function to decode JWT token
export const decodeJwtToken = (token) => {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }
    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

// Function to extract claims (role, email, modules, and modulePermissions) from token
export const getRoleFromToken = (token) => {
  const decoded = decodeJwtToken(token);
  if (decoded) {
    return {
      role: decoded.role || null,
      email: decoded.email || decoded.sub || null,
      modules: decoded.modules || [],
      modulePermissions: decoded.modulePermissions || {} // { MODULE_NAME: "READ"|"WRITE"|"DELETE"|"ADMIN" }
    };
  }
  return null;
};

// Permission level order for comparison
const PERMISSION_ORDER = { NONE: 0, BETA: 0, READ: 1, WRITE: 2, DELETE: 3, ADMIN: 4 };

/**
 * Get the permission level for a module from the JWT token.
 * SUPER_ADMIN and ADMIN roles always return "ADMIN".
 * Returns "NONE" if module not found.
 */
export const getModuleLevel = (token, moduleName) => {
  const claims = getRoleFromToken(token);
  if (!claims) return 'NONE';
  const role = claims.role?.toUpperCase();
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') return 'ADMIN';
  return claims.modulePermissions?.[moduleName] || 'NONE';
};

/**
 * Check if user's permission level for a module meets or exceeds the required level.
 * requiredLevel: "READ" | "WRITE" | "DELETE" | "ADMIN"
 */
export const hasModuleLevel = (token, moduleName, requiredLevel) => {
  const level = getModuleLevel(token, moduleName);
  return (PERMISSION_ORDER[level] || 0) >= (PERMISSION_ORDER[requiredLevel] || 0);
};

// Function to check if user has access to a specific module
export const hasModuleAccess = (token, moduleName) => {
  const claims = getRoleFromToken(token);
  if (!claims) return false;

  // SUPER_ADMIN and ADMIN have access to everything
  const role = claims.role?.toUpperCase();
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') {
    return true;
  }

  // Check modules array
  return claims.modules && claims.modules.includes(moduleName);
};

// Function to determine the redirect path based on user's modules/permissions
export const getRedirectPath = (token) => {
  const claims = getRoleFromToken(token);

  if (!claims) {
    return '/signin';
  }

  const role = claims.role?.toUpperCase();
  const modules = claims.modules || [];


  // SUPER_ADMIN and ADMIN always go to admin
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') {
    return '/admin';
  }

  // GATEMEN always go to driver
  if (role === 'GATEMEN') {
    return '/driver';
  }

  // Check for admin-level modules FIRST (before checking role)
  // This allows users with role=USER but admin modules to access admin pages
  const adminModules = [
    'USER_MANAGEMENT', 'ROLE_MANAGEMENT', 'LOCATION_MASTER',
    'CONFERENCE_ROOM', 'VEHICLE_ENROLLMENT', 'VEHICLE_ASSIGNMENT',
    'ROUTE_MANAGEMENT', 'VEHICLE_ANALYTICS', 'DASHBOARD', 'REPORTS',
    'ANALYTICS', 'SYSTEM_SETTINGS', 'AUDIT_LOG', 'JOB_REQUISITION',
    'CANDIDATE_MANAGEMENT', 'JOB_MASTER_DATA', 'JOB_DASHBOARD', 'JOB_REPORTS'
  ];

  const hasAdminModule = modules.some(m => adminModules.includes(m));

  if (hasAdminModule) {
    return '/admin';
  }

  // Check for user-level modules (Room Booking, Amenities)
  const userModules = ['ROOM_BOOKING', 'AMENITIES'];
  const hasUserModule = modules.some(m => userModules.includes(m));

  if (hasUserModule) {
    return '/user';
  }

  // USER role without admin modules goes to user page
  if (role === 'USER') {
    return '/user';
  }

  // Default: check if they have SYSTEM_LOGIN at minimum
  if (modules.includes('SYSTEM_LOGIN')) {
    return '/user'; // Default to user page if they can login but have limited modules
  }

  // No recognized modules - deny access
  return null;
};

// Check whether a JWT token is expired (or invalid)
export const isTokenExpired = (token, skewSeconds = 10) => {
  try {
    if (!token || typeof token !== 'string') {
      return true;
    }
    const decoded = decodeJwtToken(token);
    // If no exp claim, treat as expired for safety
    if (!decoded || typeof decoded.exp !== 'number') {
      return true;
    }
    const currentEpochSeconds = Math.floor(Date.now() / 1000);
    // Add small negative skew to proactively treat near-expiry as expired
    return decoded.exp <= (currentEpochSeconds + skewSeconds);
  } catch (err) {
    return true;
  }
};

// Get token expiration time in milliseconds
export const getTokenExpirationTime = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }
    const decoded = decodeJwtToken(token);
    if (!decoded || typeof decoded.exp !== 'number') {
      return null;
    }
    // Convert seconds to milliseconds
    return decoded.exp * 1000;
  } catch (err) {
    return null;
  }
};